import { useState } from 'react';
import { postPortalMessage } from '../../core/api/portal-service';
import { describeApiError } from '../../core/errors/describe-api-error';
import { useTranslation } from '../../core/hooks/use-translation';
import type { TranslationKey } from '../../core/i18n/translation';
import type { PortalMessage } from '../../core/models/portal-message';
import { Alert } from '../../ui/alert';
import { Button } from '../../ui/button';
import { Panel } from '../../ui/panel';
import { ChatIcon } from '../../ui/portal-icons';

interface MessageThreadProps {
    messages: PortalMessage[];
    /** Filed under this case when the thread is shown on a case page; null on the portal home. */
    clientCaseId: string | null;
    onPosted: (message: PortalMessage) => void;
    /** Whether older messages exist than the first one shown. */
    hasEarlier?: boolean;
    loadingEarlier?: boolean;
    onLoadEarlier?: () => void;
}

/**
 * The questions channel — the only thing a customer may write.
 *
 * The hint above the box is doing real work: the portal deliberately cannot cancel or move an
 * appointment, so the screen has to say what to do instead rather than leaving somebody hunting for
 * a button that was never built.
 *
 * **The two sides are laid out as a conversation rather than as two shades of the same block.** A
 * customer reading a thread needs to know at a glance which lines are theirs, and alignment carries
 * that on a telephone where two pastel backgrounds do not.
 */
export function MessageThread({
    messages,
    clientCaseId,
    onPosted,
    hasEarlier = false,
    loadingEarlier = false,
    onLoadEarlier
}: MessageThreadProps) {
    const { t } = useTranslation();

    const [body, setBody] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<TranslationKey | null>(null);

    async function send(event: React.FormEvent) {
        event.preventDefault();

        const trimmed = body.trim();

        if (trimmed.length === 0) {
            setError('portal.message_required');
            return;
        }

        setSending(true);
        setError(null);

        try {
            onPosted(await postPortalMessage(trimmed, clientCaseId));
            setBody('');
        } catch (cause: unknown) {
            setError(describeApiError(cause));
        } finally {
            setSending(false);
        }
    }

    return (
        <Panel title={t('portal.messages')} icon={<ChatIcon />}>
            <p className="text-sm text-slate-600 dark:text-slate-400">{t('portal.messages_hint')}</p>

            {hasEarlier && onLoadEarlier !== undefined && (
                <div className="mt-5 flex justify-center">
                    <Button variant="ghost" size="sm" disabled={loadingEarlier} onClick={onLoadEarlier}>
                        {loadingEarlier ? t('portal.loading') : t('portal.load_earlier')}
                    </Button>
                </div>
            )}

            {messages.length === 0 ? (
                <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">{t('portal.no_messages')}</p>
            ) : (
                <ul className="mt-5 space-y-4">
                    {messages.map((message) => {
                        const mine = message.authorKind === 'Customer';

                        return (
                            <li key={message.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                                <div className="max-w-[85%]">
                                    <p
                                        className={`text-xs font-semibold text-slate-500 dark:text-slate-400 ${
                                            mine ? 'text-end' : ''
                                        }`}
                                    >
                                        {mine ? t('portal.you') : (message.authorDisplayName ?? t('portal.staff'))}
                                    </p>

                                    {/* The customer's own messages carry the brand; the company's are
                                        neutral. The other way round would make the company louder in
                                        a thread the customer opened to be heard in. */}
                                    <p
                                        className={`mt-1 rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
                                            mine
                                                ? 'rounded-ee-sm bg-brand-600 text-white'
                                                : 'rounded-es-sm bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-100'
                                        }`}
                                    >
                                        {message.body}
                                    </p>

                                    {/*
                                        Shown on the customer's own messages only. Without it a question
                                        posted into silence is indistinguishable from one nobody opened.
                                    */}
                                    {mine && message.readByRecipient && (
                                        <p className="mt-1 text-end text-xs text-slate-400 dark:text-slate-500">
                                            {t('portal.seen')}
                                        </p>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            <form onSubmit={send} className="mt-5">
                {/* 16px on a telephone: iOS zooms the page into any field set smaller when it takes focus. */}
                <textarea
                    value={body}
                    onChange={(event) => setBody(event.target.value)}
                    rows={3}
                    maxLength={2048}
                    placeholder={t('portal.message_placeholder')}
                    className="w-full rounded-xl border border-slate-300 bg-white p-3 text-base text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100 sm:text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-brand-500 dark:focus:ring-brand-950"
                />

                {error !== null && (
                    <div className="mt-2">
                        <Alert>{t(error)}</Alert>
                    </div>
                )}

                <Button type="submit" disabled={sending} className="mt-3 w-full sm:w-auto">
                    {sending ? t('portal.sending') : t('portal.send')}
                </Button>
            </form>
        </Panel>
    );
}
